/**
 * A site's robot groups (Akash, 2026-10-30): how the Tasks calendar groups
 * its robot lanes. `GET/PUT /api/v1/sites/{site}/settings/robot-groups`
 * (`backend/api_server/api/site_robot_groups.py`).
 *
 * `mode: 'default'` -- one group per robot type the site has (Cleaning,
 * Delivery, Linen, Patrol, Other, in that order). `mode: 'custom'` -- the
 * site's own groups in their order, then a computed "Other robots" group
 * (`computed: true`) holding every robot in none. Every group the server
 * computed is `computed: true`; only stored groups can be renamed.
 *
 * `data === null`: this api-server predates robot groups (404), so there is
 * no robot list here either; the calendar groups its lanes by its own means
 * then. `defaultRobotGroups(robots)` reproduces the server's default
 * grouping from a robot list (the editor's "Reset" preview uses it).
 *
 * Simulated (mock) robots come back only for the ops audience: the server
 * decides, by the same rule as `useCanSeeSimulated`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchRobotGroups, saveRobotGroups } from '../v1-client';
import type { SiteId } from '../v1';

export type RobotService = 'cleaning' | 'delivery' | 'linen' | 'patrol' | 'other';
export const ROBOT_SERVICES: readonly RobotService[] = [
  'cleaning',
  'delivery',
  'linen',
  'patrol',
  'other',
];
export const ROBOT_SERVICE_LABEL: Record<RobotService, string> = {
  cleaning: 'Cleaning',
  delivery: 'Delivery',
  linen: 'Linen',
  patrol: 'Patrol',
  other: 'Other',
};
/** The computed trailing group of custom mode. */
export const OTHER_ROBOTS_GROUP_ID = '_other';
export const OTHER_ROBOTS_GROUP_NAME = 'Other robots';

export interface RobotGroupRobot {
  /** `<fleet>/<robot>`: what a group's `robots` hold. */
  key: string;
  fleet: string;
  name: string;
  service: RobotService;
  /** The robot's name, with its fleet when two robots share it. */
  label: string;
  simulated: boolean;
}

export interface RobotGroup {
  id: string;
  name: string;
  /** Robot keys, in lane order. */
  robots: string[];
  /** Not stored: every default group, and custom mode's "Other robots". */
  computed: boolean;
}

export interface StaleRobot {
  key: string;
  groupId: string;
}

export interface RobotGroupsResponse {
  mode: 'default' | 'custom';
  groups: RobotGroup[];
  robots: RobotGroupRobot[];
  /** Stored in a group but not at the site now. Kept until removed. */
  staleRobots: StaleRobot[];
  canEdit: boolean;
  /** The live robot list could not be read: robots with recent tasks stand in. */
  registryDegraded: boolean;
  updatedAtMs: number | null;
  maxGroups: number;
  maxNameLength: number;
}

export type RobotGroupsUpdate =
  | { reset: true }
  | { groups: Array<{ id?: string; name: string; robots: string[] }> };

export const ROBOT_GROUPS_KEY = 'robot-groups' as const;
export function robotGroupsKey(siteId: string | null | undefined) {
  return [ROBOT_GROUPS_KEY, siteId ?? null] as const;
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function strList(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
}

function service(v: unknown): RobotService {
  return (ROBOT_SERVICES as readonly unknown[]).includes(v) ? (v as RobotService) : 'other';
}

/** Defensive: a missing or malformed field becomes its empty value. */
export function adaptRobotGroups(raw: unknown): RobotGroupsResponse {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const robots: RobotGroupRobot[] = (Array.isArray(o.robots) ? o.robots : []).flatMap((r) => {
    const x = (r ?? {}) as Record<string, unknown>;
    const key = str(x.key);
    if (!key) return [];
    const name = str(x.name) ?? key.slice(key.indexOf('/') + 1);
    return [
      {
        key,
        fleet: str(x.fleet) ?? key.split('/')[0] ?? '',
        name,
        service: service(x.service),
        label: str(x.label) ?? name,
        simulated: x.simulated === true,
      },
    ];
  });
  const groups: RobotGroup[] = (Array.isArray(o.groups) ? o.groups : []).flatMap((g) => {
    const x = (g ?? {}) as Record<string, unknown>;
    const id = str(x.id);
    const name = str(x.name);
    if (!id || name === null) return [];
    return [{ id, name, robots: strList(x.robots), computed: x.computed === true }];
  });
  const staleRobots: StaleRobot[] = (Array.isArray(o.staleRobots) ? o.staleRobots : []).flatMap(
    (s) => {
      const x = (s ?? {}) as Record<string, unknown>;
      const key = str(x.key);
      const groupId = str(x.groupId);
      return key && groupId ? [{ key, groupId }] : [];
    },
  );
  const num = (v: unknown, d: number) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  return {
    mode: o.mode === 'custom' ? 'custom' : 'default',
    groups,
    robots,
    staleRobots,
    canEdit: o.canEdit === true,
    registryDegraded: o.registryDegraded === true,
    updatedAtMs: typeof o.updatedAtMs === 'number' ? o.updatedAtMs : null,
    maxGroups: num(o.maxGroups, 20),
    maxNameLength: num(o.maxNameLength, 40),
  };
}

/** The default grouping, as the server computes it: one group per service
 *  present, in service order, robots in the order given. */
export function defaultRobotGroups(robots: readonly RobotGroupRobot[]): RobotGroup[] {
  return ROBOT_SERVICES.flatMap((svc) => {
    const keys = robots.filter((r) => r.service === svc).map((r) => r.key);
    return keys.length
      ? [{ id: `service:${svc}`, name: ROBOT_SERVICE_LABEL[svc], robots: keys, computed: true }]
      : [];
  });
}

/** `data === null`: this api-server has no robot groups yet. */
export function useRobotGroups(siteId: string | null | undefined) {
  return useQuery<RobotGroupsResponse | null>({
    queryKey: robotGroupsKey(siteId),
    queryFn: async () => {
      const raw = await fetchRobotGroups(siteId as SiteId);
      return raw === null ? null : adaptRobotGroups(raw);
    },
    enabled: Boolean(siteId),
    staleTime: 60_000,
  });
}

export function useSaveRobotGroups(siteId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation<RobotGroupsResponse, Error, RobotGroupsUpdate>({
    mutationFn: async (body) => adaptRobotGroups(await saveRobotGroups(siteId as SiteId, body)),
    onSuccess: (saved) => {
      qc.setQueryData(robotGroupsKey(siteId), saved);
      // Every reader of the grouping (the calendar's lanes) refetches.
      void qc.invalidateQueries({ queryKey: robotGroupsKey(siteId) });
    },
  });
}
